import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { LiveStreamWithEventResponse, WebSocketEventData } from '@shared/types';
import { isLiveEventStatus, isCompletedEventStatus } from '@shared/types';
import { API_BASE_URL } from '@/client/config';
import { handleApiError } from '@/client/lib/apiError';
import { error } from 'console';

interface LiveStreamsState {
  // Legacy API data (from /api/live-streams)
  liveStreams: LiveStreamWithEventResponse[];

  // WebSocket-driven data
  liveEvents: WebSocketEventData[]; // Events with status: starting, live, on break, stopping
  completedEvents: WebSocketEventData[]; // Events with status: available (that were previously live)
  eventMap: Map<string, WebSocketEventData>; // All events that have been live (for re-streaming support)

  loading: boolean;
  error: string | null;

  // Legacy setters
  setLiveStreams: (liveStreams: LiveStreamWithEventResponse[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  fetchLiveStreams: (accessToken: string, logout: () => void) => Promise<void>;

  // WebSocket actions
  updateEventStatus: (eventData: WebSocketEventData) => void;
  hasEverBeenLive: (eventID: string) => boolean;
}

export const useLiveStreamsStore = create<LiveStreamsState>()(devtools((set, get) => ({
  // Initial state
  liveStreams: [],
  liveEvents: [],
  completedEvents: [],
  eventMap: new Map(),
  loading: false,
  error: null,

  // Legacy setters
  setLiveStreams: (liveStreams) => set({ liveStreams, error: null }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),

  // Fetch live streams from API (used for initial page load)
  // Note: API returns LiveStreamWithEventResponse[], which is different from WebSocketEventData
  // For now, we just populate the legacy liveStreams field
  // The WebSocket will handle populating liveEvents with full event data
  fetchLiveStreams: async (accessToken: string, logout: () => void) => {
    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/api/live-streams`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (handleApiError(response, logout)) return;
        throw new Error(`Failed to fetch live streams: ${response.statusText}`);
      }

      const data = await response.json();

      // Store legacy API data
      set({ liveStreams: data.liveStreams, loading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      set({ error: errorMessage, loading: false });
    }
  },

  /**
   * Update event status from WebSocket message
   * Handles the full event lifecycle and movement between sections
   */
  updateEventStatus: (eventData: WebSocketEventData) => {
    set((state) => {
      const { eventID, eventStatus } = eventData;

      // Create new eventMap to update immutably
      const newEventMap = new Map(state.eventMap);

      if (isLiveEventStatus(eventStatus)) {
        // Event is live (starting, live, on break, stopping)

        // Track this event as having been live
        newEventMap.set(eventID, eventData);

        // Check if event is in completedEvents - if so, move it to liveEvents
        const inCompleted = state.completedEvents.some(e => e.eventID === eventID);

        if (inCompleted) {
          // Re-streaming: move from completed back to live
          return {
            ...state,
            liveEvents: [...state.liveEvents, eventData],
            completedEvents: state.completedEvents.filter(e => e.eventID !== eventID),
            eventMap: newEventMap,
          };
        } else {
          // Check if event already exists in liveEvents - update in place
          const existingIndex = state.liveEvents.findIndex(e => e.eventID === eventID);

          if (existingIndex >= 0) {
            // Update existing live event in place
            const newLiveEvents = [...state.liveEvents];
            newLiveEvents[existingIndex] = eventData;
            return {
              ...state,
              liveEvents: newLiveEvents,
              eventMap: newEventMap,
            };
          } else {
            // New event going live
            return {
              ...state,
              liveEvents: [...state.liveEvents, eventData],
              eventMap: newEventMap,
            };
          }
        }
      } else if (isCompletedEventStatus(eventStatus)) {
        // Event is now "available"
        // Only track if event was previously live (in eventMap or liveEvents)
        const wasLive = state.eventMap.has(eventID) || state.liveEvents.some(e => e.eventID === eventID);

        if (wasLive) {
          // Track in eventMap for re-streaming support
          newEventMap.set(eventID, eventData);

          // Move from liveEvents to completedEvents
          const newLiveEvents = state.liveEvents.filter(e => e.eventID !== eventID);

          return {
            ...state,
            liveEvents: newLiveEvents,
            completedEvents: [...state.completedEvents.filter(e => e.eventID !== eventID), eventData],
            eventMap: newEventMap,
          };
        } else {
          // Don't track - this was likely a VOD upload that never streamed
          return state;
        }
      } else {
        // Other status (e.g., "new", "scheduled", "cancelled")
        // Only update if we're already tracking this event
        if (state.eventMap.has(eventID)) {
          newEventMap.set(eventID, eventData);
          return {
            ...state,
            eventMap: newEventMap,
          };
        } else {
          // Not tracking this event
          return state;
        }
      }
    });
  },

  /**
   * Check if an event has ever been live (for determining if we should track it)
   */
  hasEverBeenLive: (eventID: string) => {
    return get().eventMap.has(eventID);
  },

}), { name: 'live-streams-store' }));
